import React from 'react';
import { noop } from 'lodash';
import { useHistory } from 'react-router-dom';
import qs from 'qs';

import * as auth from '../../api/auth';
import { getClientSummary, getCurrentUser, signupNewUser } from '../../api/users';
import { getReferrerCode, removeReferrerCode, storeReferrerCode } from '../../utils/referrer';
import { removeJwt, getJwt, setJwt } from '../../utils/jwt';
import {
	dispatchLogin,
	dispatchLogout,
	dispatchUserBalanceUpdate,
	eventsRootEl,
	UNAUTHORIZED_API,
} from '../../utils/eventDispatchers';
import { JWTStatuses } from '../../constants/jwt';
import { useInactivityEffect } from './useInactivityEffect';
import InactivityModal from './InactivityModal';
import appConfig from '../../appConfig';
import { ClientUserBonus } from '../../@types/ClientUserBonus';

const isBooki = appConfig.brand.name === 'Booki';
interface PortalSessionContextValue {
	login: (user: string, password: string) => Promise<LoginResponse>;
	logout: () => void;
	sessionTime: null | undefined | number;
	signUpUser: (newUser: UserSignup) => Promise<SignupResponse>;
	refreshPortalUser: () => void;
	refreshClientUserBonuses: (clientId: number) => void;
	user?: PortalUser | null;
	clientUserBonuses: Record<number, Array<ClientUserBonus>>;
}

const PortalSessionContext = React.createContext<PortalSessionContextValue>({
	login: async (...args) => ({ errors: { error: ['Context not ready'] } }),
	logout: async (...args) => ({ errors: { error: ['Context not ready'] } }),
	signUpUser: async (...args) => ({ errors: { error: ['Context not ready'] } }),
	refreshPortalUser: noop,
	refreshClientUserBonuses: noop,
	clientUserBonuses: {},
	sessionTime: null,
	user: null,
});

const USER_POLL_INTERVAL = 60000;

export const PortalSessionProvider: React.FC = ({ children }) => {
	const refreshingTokenRef = React.useRef(false);
	const refreshingUserRef = React.useRef(false);
	const [jwt, setPortalJwt] = React.useState<string | null>(getJwt());
	const [user, setUser] = React.useState<PortalUser | null>(loadUser());
	const [clientUserBonuses, setClientUserBonuses] = React.useState<PortalSessionContextValue['clientUserBonuses']>({});
	const [sessionTime, setSessionTime] = React.useState<null | undefined | number>(null);
	const [showInactivityModal, setShowInactivityModal] = React.useState(false);
	const history = useHistory();

	const removeAuthdata = () => {
		dispatchLogout(); // dispatch to client apps like wagering so they will logout too
		removeJwt();
		setPortalJwt(null);
		setUser(null);
		setSessionTime(null);
		setClientUserBonuses({});
	};

	React.useEffect(() => {
		if (isBooki) {
			return;
		}
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${appConfig.recaptchaSiteId}`;
		document.head.appendChild(script);
	}, []);

	const login = React.useCallback(async (userNameOrEmail: string, password: string) => {
		const token =
			isBooki || process.env.NODE_ENV === 'development'
				? undefined
				: appConfig?.recaptchaSiteId
				? await window.grecaptcha.enterprise.execute(appConfig.recaptchaSiteId, {
						action: 'login',
				  })
				: undefined;

		const loginRes = await auth.login({
			email: userNameOrEmail,
			password,
			recaptcha_token: token,
			platform: 'web',
		});

		if (loginRes.data?.status === 'success' && loginRes.data?.token) {
			dispatchLogin();
			setJwt(loginRes.data.token);
			setPortalJwt(loginRes.data.token);
		}

		return loginRes;
	}, []);

	const logout = React.useCallback(() => {
		auth
			.logout()
			.catch((error) => {
				// in some cases logout will fail because the token is already unauthorized, we can look at the
				// error meta to find why
				if (error?.meta?.token_status) {
				}
			})
			.finally(() => {
				// whether the request was successful or not it's safe to remove session data
				removeAuthdata();
			});
	}, []);

	useInactivityEffect(
		React.useCallback(() => {
			logout();
			setShowInactivityModal(true);
		}, [logout]),
		!!user
	);

	const signUpUser = React.useCallback(async (newUser: UserSignup) => {
		const signupRes = await signupNewUser({
			...newUser,
			affiliate_token: getReferrerCode(),
		});
		removeReferrerCode();

		return signupRes;
	}, []);

	const refreshPortalUser = React.useCallback(() => {
		if (refreshingUserRef.current) return;
		refreshingUserRef.current = true;
		getCurrentUser()
			.then((response) => {
				if (response.user?.id) setUser(response.user);

				if (response.sessionTime) {
					setSessionTime(parseInt(response.sessionTime, 10));
				}
			})
			.catch((e) => {
				// error while refreshing the user, no action needed, if it was a 401 unauthorized issue
				// it will be dealt with in the dedicated effect
			})
			.finally(() => {
				refreshingUserRef.current = false;
			});
	}, []);

	const refreshClientUserBonuses = React.useCallback(async (clientId) => {
		const clientBonuses = await getClientSummary(clientId);

		if (!clientBonuses.data) return;

		setClientUserBonuses((oldBonuses) => ({
			...oldBonuses,
			[clientId]: clientBonuses.data,
		}));
	}, []);

	// On initialisation store referral code and set up listeners for unauthorised api responses
	React.useEffect(() => {
		storeReferrerCode();

		const unauthHandler = (event) => {
			if (event?.detail?.tokenStatus?.id === JWTStatuses.EXPIRED) {
				if (refreshingTokenRef.current) return;
				refreshingTokenRef.current = true;

				// console.log('token expired, trying to restore');
				auth
					.refreshToken()
					.then((refreshResponse) => {
						// console.log('token expired resp', refreshResponse);
						if (refreshResponse.data?.status === 'success' && refreshResponse.data?.token) {
							dispatchLogin();
							setJwt(refreshResponse.data.token);
							setPortalJwt(refreshResponse.data.token);
						}
					})
					.catch((error) => {
						// console.log('error while refreshing the token', error);
						logout();
					})
					.finally(() => {
						refreshingTokenRef.current = false;
					});
			} else {
				// console.log('token invalid or missing, removing local auth information');
				removeAuthdata();
			}
		};

		eventsRootEl?.addEventListener(UNAUTHORIZED_API, unauthHandler);

		return () => {
			eventsRootEl?.removeEventListener(UNAUTHORIZED_API, unauthHandler);
		};
	}, [logout]);

	// This effect will update the user if it hasn't been updated in the defined interval
	React.useEffect(() => {
		if (user) {
			const interval = setTimeout(refreshPortalUser, USER_POLL_INTERVAL);

			return () => {
				clearTimeout(interval);
			};
		}
	}, [refreshPortalUser, user]);

	React.useEffect(() => {
		// if jwt changes the user has just logged in
		if (jwt) {
			refreshPortalUser();
		}
	}, [jwt, refreshPortalUser]);

	// keep an updated version of the user in local storage
	React.useEffect(() => {
		saveUser(user);
	}, [user]);

	// Dispatch this event to notify client apps of a balance update
	React.useEffect(() => {
		if (user?.balance !== 0 && !user?.balance) return;

		dispatchUserBalanceUpdate(user.balance);
	}, [user?.balance]);

	const showLogin = () => {
		setShowInactivityModal(false);
		const search = qs.parse(window.location.search || '', {
			ignoreQueryPrefix: true,
		});

		history.replace({
			pathname: window.location.pathname,
			search: qs.stringify({ ...search, modal: 'login' }, { addQueryPrefix: true }),
		});
	};

	return (
		<PortalSessionContext.Provider
			value={{
				login,
				logout,
				sessionTime,
				signUpUser,
				clientUserBonuses,
				refreshPortalUser,
				refreshClientUserBonuses,
				user,
			}}
		>
			{children}
			<InactivityModal
				onClose={() => setShowInactivityModal(false)}
				onLogin={showLogin}
				visible={showInactivityModal}
			/>
		</PortalSessionContext.Provider>
	);
};

export default PortalSessionContext;

// Functions to store/load in localstorage a copy of the user object

const USER_LOCAL_STORAGE_KEY = 'user_cache';

export const loadUser = () => {
	try {
		const serialisedUser = window.localStorage.getItem(USER_LOCAL_STORAGE_KEY);

		if (serialisedUser && !getJwt()) throw new Error('JWT token missing');

		return serialisedUser ? (JSON.parse(serialisedUser) as PortalUser) : null;
	} catch (e) {
		window.localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
		return null;
	}
};

export const saveUser = (user: PortalUser | null) => {
	if (!user) window.localStorage.removeItem(USER_LOCAL_STORAGE_KEY);
	else window.localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(user));
};
